<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import Avatar from 'next/avatar/Avatar.vue';
import { useKeyboardNavigableList } from 'dashboard/composables/useKeyboardNavigableList';

const props = defineProps({
  items: { type: Array, default: () => [] },
  searchKey: { type: String, default: '' },
});

const emit = defineEmits(['select']);
const listRef = ref(null);
const selectedIndex = ref(0);

const participantPhone = participant =>
  String(participant.phone_number || participant.phoneNumber || '').replace(
    /\D/g,
    ''
  );

const participantName = participant =>
  participant.saved_name ||
  participant.savedName ||
  participant.display_name ||
  participant.displayName ||
  participantPhone(participant);

const filteredItems = computed(() => {
  const search = props.searchKey.trim().toLocaleLowerCase();
  return props.items.filter(participant => {
    if (!search) return true;
    return [participantName(participant), participantPhone(participant)].some(
      value =>
        String(value || '')
          .toLocaleLowerCase()
          .includes(search)
    );
  });
});

const adjustScroll = () => {
  nextTick(() => {
    listRef.value
      ?.querySelector(`#whatsapp-mention-${selectedIndex.value}`)
      ?.scrollIntoView({ block: 'nearest' });
  });
};

const selectParticipant = index => {
  const participant = filteredItems.value[index];
  if (participant) emit('select', participant);
};

useKeyboardNavigableList({
  items: filteredItems,
  onSelect: () => selectParticipant(selectedIndex.value),
  adjustScroll,
  selectedIndex,
});

watch(filteredItems, items => {
  if (selectedIndex.value >= items.length) selectedIndex.value = 0;
});
</script>

<template>
  <div>
    <ul
      v-if="filteredItems.length"
      ref="listRef"
      class="mention--box absolute bottom-full left-0 z-20 mb-0 max-h-[12.5rem] w-full overflow-auto rounded-xl border border-solid border-n-strong bg-n-solid-1 p-1 text-sm shadow-md"
      role="listbox"
    >
      <li
        v-for="(participant, index) in filteredItems"
        :id="`whatsapp-mention-${index}`"
        :key="
          participant.jid || participant.id || participantPhone(participant)
        "
      >
        <button
          type="button"
          class="flex w-full items-center gap-2 rounded-md px-2 py-1 text-left hover:bg-n-alpha-black2"
          :class="{ 'bg-n-alpha-black2': index === selectedIndex }"
          @mouseover="selectedIndex = index"
          @click="selectParticipant(index)"
        >
          <Avatar
            :src="participant.img_url || participant.imgUrl"
            :name="participantName(participant)"
            :size="28"
          />
          <span class="min-w-0 flex-1">
            <span class="block truncate font-medium text-n-slate-12">
              {{ participantName(participant) }}
            </span>
            <span class="block truncate text-xs text-n-slate-10">
              {{
                $t('INTERNAL_CHAT.GROUP_PHONE', {
                  phone: participantPhone(participant),
                })
              }}
            </span>
          </span>
        </button>
      </li>
    </ul>
  </div>
</template>
